import {
  RichText as CmsRichText,
  type NodeRendererType,
  type RichTextProps,
} from '@graphcms/rich-text-react-renderer';
import type { ImageProps } from '@graphcms/rich-text-types';
import { Box, Heading, Strong, Text, TextLink } from 'braid-design-system';
import React from 'react';

import * as styles from './RichText.css';

export const RichText = (props: RichTextProps) => (
  <CmsRichText renderers={renderers} {...props} />
);

export const renderers: NodeRendererType = {
  h1: ({ children }) => (
    <Box marginTop={'xxlarge'} marginBottom={'xlarge'}>
      <Heading level="1">{children}</Heading>
    </Box>
  ),
  h2: ({ children }) => (
    <Box marginTop={'xlarge'} marginBottom={'large'}>
      <Heading level="2">{children}</Heading>
    </Box>
  ),
  h3: ({ children }) => (
    <Box marginTop={'large'} marginBottom={'large'}>
      <Heading level="3">{children}</Heading>
    </Box>
  ),
  h4: ({ children }) => (
    <Box marginTop={'large'} marginBottom={'gutter'}>
      <Heading level="4">{children}</Heading>
    </Box>
  ),
  h5: ({ children }) => (
    <Box marginTop={'large'} marginBottom={'gutter'}>
      <Heading level="4" component="h5">
        {children}
      </Heading>
    </Box>
  ),
  h6: ({ children }) => (
    <Box marginTop={'large'} marginBottom={'gutter'}>
      <Heading level="4" component="h6">
        {children}
      </Heading>
    </Box>
  ),
  p: ({ children }) => {
    if (React.isValidElement(children)) {
      const updatedChildren: React.ReactElement = React.cloneElement(children, {
        ...children.props,
        content: children.props.content.filter((c: any) =>
          Boolean(c.type || c?.text?.trim()),
        ),
      });

      return updatedChildren.props.content.length ? (
        <Box marginY={'gutter'}>
          <Text component="p">{updatedChildren}</Text>
        </Box>
      ) : (
        <></>
      );
    }
    return <></>;
  },
  bold: ({ children }) => <Strong>{children}</Strong>,
  underline: ({ children }) => <u>{children}</u>,
  italic: ({ children }) => <i>{children}</i>,
  a: ({ children, href }) => (
    <TextLink href={`${href || '#!'}`}>{children}</TextLink>
  ),
  ul: ({ children }) => <ul>{children}</ul>,
  ol: ({ children }) => <ol>{children}</ol>,
  li: ({ children }) => (
    <Box className={styles.ListStyle}>
      <li>{children}</li>
    </Box>
  ),
  table: ({ children }) => (
    <table className={styles.TableStyle}>{children}</table>
  ),
  table_body: ({ children }) => (
    <tbody className={styles.TbodyStyle}>{children}</tbody>
  ),
  table_row: ({ children }) => <tr className={styles.TrStyle}>{children}</tr>,
  table_cell: ({ children }) => (
    <td className={styles.TcellStyle}>{children}</td>
  ),
  table_head: ({ children }) => (
    <Box className={styles.TheadStyle} component="thead" background="neutral">
      {children}
    </Box>
  ),
  table_header_cell: ({ children }) => (
    <th className={styles.ThStyle}>{children}</th>
  ),
  code: ({ children }) => <code>{children}</code>,
  code_block: ({ children }) => (
    <Box
      background="neutralLight"
      padding="small"
      boxShadow="borderNeutralLight"
    >
      <Text>
        <code>{children}</code>
      </Text>
    </Box>
  ),
  blockquote: ({ children }) => (
    <Text>
      <Box
        className={styles.BlockQuoteStyle}
        marginLeft="xsmall"
        paddingLeft="small"
      >
        {children}
      </Box>
    </Text>
  ),
  class: ({ children, className }) => (
    <Box className={className}>{children}</Box>
  ),
  // RichText renderer uses altText prop for image element, but articles migrated with custom patch (https://myseek.atlassian.net/browse/CU-514) is using alt tag.
  img: ({
    alt,
    altText,
    height,
    src,
    title,
    width,
  }: Partial<ImageProps> & { alt?: string }) => (
    <Box marginX={'none'} marginY={'medium'}>
      <img
        src={src}
        alt={altText || alt}
        title={title}
        width="100%"
        style={{
          maxWidth: width,
          maxHeight: height,
        }}
      />
    </Box>
  ),
  iframe: ({ height, width, url }) => (
    <Box marginX={'small'} marginY={'medium'}>
      <iframe
        src={url}
        height={height}
        width={width}
        style={{
          width: '100%',
          height: '100%',
        }}
      />
    </Box>
  ),
};
